// Preview per-USUARIO NO-persistido (Item E #2). Estado de SESIÓN local (per-tab, en memoria)
// que deja al operador no-root "echar un vistazo" a la versión nueva SIN tocar la variante del
// comercio ni el switch durable.
//
// CRÍTICO — separación del cache de A1: este estado vive en un singleton de módulo (memoria del
// tab), NUNCA en `FeatureVersionCacheEntry`/localStorage. Si lo metiéramos en ese cache, el GET
// AUTORITATIVO de A1 (useFeatureVersion: el backend GANA) lo borraría en la próxima hidratación,
// y peor: contaminaría la variante per-comercio que A1 reconcilia. El preview es del USUARIO y de
// la PESTAÑA; al recargar desaparece (es un "vistazo", no una preferencia).
//
// El opt-out durable del prompt de feedback (E #3) SÍ se persiste (localStorage, per-usuario) —
// es una preferencia del operador, no estado de variante; usa una llave dedicada que el
// resetStore del login NO toca (mismo patrón que electronPrintersConfig).

import { MENU_FEATURE_KEY } from './constants';

type Listener = () => void;

// ── Estado per-tab del preview activo (en memoria, NO persistido) ───────────────────────
// Keyed por feature_key. true = el usuario está viendo la vista previa de la versión nueva.
const activePreview = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

export function isPreviewActive(featureKey: string): boolean {
  return activePreview.get(featureKey) === true;
}

export function subscribePreview(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// Enciende la vista previa per-usuario. No escribe cache ni outbox: es estado de tab puro.
export function startPreview(featureKey: string): void {
  if (activePreview.get(featureKey) === true) return;
  activePreview.set(featureKey, true);
  notify(featureKey);
}

// Apaga la vista previa ("Volver a la actual"). Devuelve al operador a la variante del comercio.
export function stopPreview(featureKey: string): void {
  if (activePreview.get(featureKey) !== true) return;
  activePreview.set(featureKey, false);
  notify(featureKey);
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal el preview del tab no debe sobrevivir
// (es del usuario anterior). Coherente con resetFeatureVersionStore/Hydration/Draining.
export function resetPreviewState(): void {
  const keys = Array.from(activePreview.keys());
  activePreview.clear();
  keys.forEach((k) => notify(k));
}

// ── Opt-out DURABLE del prompt de feedback por uso (per-usuario) ─────────────────────────
// Persiste en localStorage con llave dedicada que el resetStore del login NO borra. No es
// estado de variante: es "no me preguntes de nuevo" del operador. Per-usuario para que dos
// operadores del mismo dispositivo no compartan la preferencia.
const FEEDBACK_OPTOUT_PREFIX = 'plick:rollout-usage-feedback-optout:v1:';

function userId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('id_user') ?? '';
  } catch {
    return '';
  }
}

function optOutKey(featureKey: string, uid: string): string {
  return `${FEEDBACK_OPTOUT_PREFIX}${uid}:${featureKey}`;
}

export function hasOptedOutUsageFeedback(featureKey = MENU_FEATURE_KEY): boolean {
  if (typeof window === 'undefined') return false;
  const uid = userId();
  if (!uid) return false;
  try {
    return window.localStorage.getItem(optOutKey(featureKey, uid)) === '1';
  } catch {
    return false;
  }
}

export function optOutUsageFeedback(featureKey = MENU_FEATURE_KEY): void {
  if (typeof window === 'undefined') return;
  const uid = userId();
  if (!uid) return;
  try {
    window.localStorage.setItem(optOutKey(featureKey, uid), '1');
  } catch {
    // ignore quota / private-mode
  }
}

// ── Cap per-usuario del prompt de feedback (per-tab, en memoria) ─────────────────────────
// El prompt es OMITIBLE y se muestra a lo sumo una vez por sesión de uso. Marcamos en memoria
// que ya se mostró para no repetirlo tras cada N acciones dentro del mismo tab.
const feedbackShown = new Set<string>();

export function wasUsageFeedbackShown(featureKey: string): boolean {
  return feedbackShown.has(featureKey);
}

export function markUsageFeedbackShown(featureKey: string): void {
  feedbackShown.add(featureKey);
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetPreviewForTests(): void {
  activePreview.clear();
  listeners.clear();
  feedbackShown.clear();
}
